// src/pages/IndicateurODD.jsx
import React, { useState, useEffect, useMemo } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import DownloadIcon from '@mui/icons-material/Download';
import {
    Container,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Button,
    Card,
    Tabs,
    Tab,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MuiCardContent from '@mui/material/CardContent';
import Footer from '../navbar/Footer';
import { getPublications } from '../services/publicationService';

const CardContent = styled(MuiCardContent)(`
  display: flex;
  flex-direction: row;
  padding: 16px;
`);

function IndicateurODD() {
    const [pndData, setPndData] = useState([]);
    const [oddData, setOddData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination et filtres
    const [pndVisibleCount, setPndVisibleCount] = useState(2);
    const [oddVisibleCount, setOddVisibleCount] = useState(2);
    const [pndFilterYear, setPndFilterYear] = useState('all');
    const [oddFilterYear, setOddFilterYear] = useState('all');
    const [activeTab, setActiveTab] = useState(0);

    // Chargement des données depuis l'API
    useEffect(() => {
        setLoading(true);
        Promise.all([
            getPublications({ type: 'ipnd', perPage: 1000 }),
            getPublications({ type: 'iodd', perPage: 1000 })
        ])
            .then(([resPnd, resOdd]) => {
                if (resPnd.error) setError(resPnd.error);
                else if (resOdd.error) setError(resOdd.error);
                else {
                    // Mapper les données PND
                    const mappedPnd = resPnd.data.map(item => ({
                        title: item.title,
                        link: item.document_url,
                        imageLink: item.image_url,
                        description: item.description,
                        publicationDate: new Date(item.publication_date),
                    }));
                    // Mapper les données ODD
                    const mappedOdd = resOdd.data.map(item => ({
                        title: item.title,
                        link: item.document_url,
                        imageLink: item.image_url,
                        description: item.description,
                        publicationDate: new Date(item.publication_date),
                    }));
                    // Trier par date de publication décroissante
                    mappedPnd.sort((a, b) => b.publicationDate - a.publicationDate);
                    mappedOdd.sort((a, b) => b.publicationDate - a.publicationDate);
                    setPndData(mappedPnd);
                    setOddData(mappedOdd);
                }
            })
            .catch(err => {
                setError('Erreur lors du chargement des données.');
                console.error(err);
            })
            .finally(() => setLoading(false));
    }, []);

    // Années disponibles (union des deux jeux de données)
    const allYears = useMemo(() => {
        const yearsSet = new Set();
        [...pndData, ...oddData].forEach(item => {
            yearsSet.add(item.publicationDate.getFullYear());
        });
        return [...yearsSet].sort((a, b) => b - a);
    }, [pndData, oddData]);

    // Filtrage PND
    const filteredPndData = useMemo(() => {
        if (pndFilterYear === 'all') return pndData;
        return pndData.filter(item => item.publicationDate.getFullYear() === parseInt(pndFilterYear));
    }, [pndData, pndFilterYear]);

    // Filtrage ODD
    const filteredOddData = useMemo(() => {
        if (oddFilterYear === 'all') return oddData;
        return oddData.filter(item => item.publicationDate.getFullYear() === parseInt(oddFilterYear));
    }, [oddData, oddFilterYear]);

    // Gestionnaires
    const handleChangeTab = (event, newValue) => setActiveTab(newValue);

    const handleLoadMorePnd = () => setPndVisibleCount(prev => prev + 2);
    const handleLoadMoreOdd = () => setOddVisibleCount(prev => prev + 2);

    const handleFilterChangePnd = (event) => {
        setPndFilterYear(event.target.value);
        setPndVisibleCount(2);
    };
    const handleFilterChangeOdd = (event) => {
        setOddFilterYear(event.target.value);
        setOddVisibleCount(2);
    };

    const getYear = (dateString) => new Date(dateString).getFullYear();

    // Styles (reproduits de la version statique)
    const cardStyle = {
        marginBottom: '10px',
        border: '1px solid #ddd',
        borderRadius: '5px',
    };
    const imageStyle = {
        width: '80px',
        height: 'auto',
        marginRight: '20px',
        borderRadius: '5px',
        display: 'block',
    };
    const contentStyle = { flex: '1' };
    const menuItemStyle = { color: 'green' };

    if (loading) {
        return (
            <div style={{ backgroundColor: '#f5f5f5', paddingBottom: '10px' }}>
                <NavigationBar />
                <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
                    <Typography variant="body1">Chargement des données...</Typography>
                </Container>
                <Footer />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ backgroundColor: '#f5f5f5', paddingBottom: '10px' }}>
                <NavigationBar />
                <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
                    <Typography variant="body1" color="error">{error}</Typography>
                </Container>
                <Footer />
            </div>
        );
    }

    return (
        <div style={{ backgroundColor: '#f5f5f5', paddingBottom: '10px' }}>
            <NavigationBar />
            <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleChangeTab}
                        textColor="inherit"
                        indicatorColor="secondary"
                    >
                        <Tab label="PND" />
                        <Tab label="ODD" />
                    </Tabs>
                </div>

                {/* Section PND */}
                {activeTab === 0 && (
                    <>
                        <Typography variant="h6" align="justify" gutterBottom>
                            Le Plan National de Développement (PND)
                        </Typography>
                        <Typography variant="body1" align="justify" paragraph>
                            Cette section explore les rapports et documents liés au Plan National de Développement (PND) du Burundi. Le rapport sur les Indicateurs du PND constitue un outil crucial de planification stratégique, visant à guider le Burundi vers son émergence d'ici 2027. Baptisé "Plan National de Développement 2018-2027", il aborde l'ensemble des défis de développement auxquels le pays fera face au cours des dix prochaines années. Ce projet ambitieux met l'accent sur la nécessité d'une synergie entre les enjeux environnementaux, sociaux et économiques, qui doivent être considérés de manière intégrée, en adoptant de nouveaux modèles économiques et des modes de vie innovants.
                        </Typography>

                        <FormControl fullWidth margin="normal" style={{ marginLeft: '0px', width: '300px' }}>
                            <InputLabel id="year-filter-annuaires-label">Sélectionnez par l'année de publication (PND)</InputLabel>
                            <Select
                                labelId="year-filter-annuaires-label"
                                id="year-filter-annuaires"
                                value={pndFilterYear}
                                label="Sélectionnez par l'année de publication (PND)"
                                onChange={handleFilterChangePnd}
                            >
                                <MenuItem value="all">Tous les indicateurs (PND)</MenuItem>
                                {allYears.map(year => (
                                    <MenuItem key={year} value={year} style={menuItemStyle}>{year}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {filteredPndData.slice(0, pndVisibleCount).map((item, index) => (
                            <Card key={index} style={cardStyle}>
                                <CardContent>
                                    <a href={item.link} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'flex-start', textDecoration: 'none', color: 'inherit' }}>
                                        <Box>
                                            <img src={item.imageLink} alt={item.title} style={imageStyle} />
                                        </Box>
                                        <Box style={contentStyle}>
                                            <Typography variant="h6" component="div" style={{ color: 'green' }}>
                                                <DownloadIcon /> {item.title}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {item.description}
                                            </Typography>
                                        </Box>
                                    </a>
                                </CardContent>
                            </Card>
                        ))}

                        {pndVisibleCount < filteredPndData.length && (
                            <Button
                                onClick={handleLoadMorePnd}
                                style={{
                                    marginTop: '20px',
                                    backgroundColor: '#388E3C',
                                    color: 'white',
                                }}
                            >
                                Plus des indicateurs (PND)
                            </Button>
                        )}
                    </>
                )}

                {/* Section ODD */}
                {activeTab === 1 && (
                    <>
                        <Typography variant="h6" align="justify" gutterBottom>
                            Les objectifs de développement durable (ODD)
                        </Typography>
                        <Typography variant="body1" align="justify" paragraph>
                            Cette section met en lumière les rapports et documents en lien avec les Objectifs de Développement Durable (ODD). Le rapport d'actualisation des indicateurs ODD résulte des efforts collaboratifs du Comité multisectoriel dédié à la surveillance des indicateurs de mise en œuvre des programmes de développement, incluant les ODD et le Plan National de Développement (PND), tout ceci avec le soutien financier du Programme des Nations Unies pour le Développement (PNUD).
                            Cette actualisation annuelle fournit au Gouvernement des données précieuses pour ajuster efficacement sa politique en vue d'atteindre la vision 2030.
                        </Typography>

                        <FormControl fullWidth margin="normal" style={{ marginLeft: '0px', width: '300px' }}>
                            <InputLabel id="year-filter-odd-label">Sélectionnez par l'année de publication (ODD)</InputLabel>
                            <Select
                                labelId="year-filter-odd-label"
                                id="year-filter-odd"
                                value={oddFilterYear}
                                label="Sélectionnez par l'année de publication (ODD)"
                                onChange={handleFilterChangeOdd}
                            >
                                <MenuItem value="all">Tous les indicateurs (ODD)</MenuItem>
                                {allYears.map(year => (
                                    <MenuItem key={year} value={year} style={menuItemStyle}>{year}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {filteredOddData.slice(0, oddVisibleCount).map((item, index) => (
                            <Card key={index} style={cardStyle}>
                                <CardContent>
                                    <a href={item.link} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'flex-start', textDecoration: 'none', color: 'inherit' }}>
                                        <Box>
                                            <img src={item.imageLink} alt={item.title} style={imageStyle} />
                                        </Box>
                                        <Box style={contentStyle}>
                                            <Typography variant="h6" component="div" style={{ color: 'green' }}>
                                                <DownloadIcon /> {item.title}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {item.description}
                                            </Typography>
                                        </Box>
                                    </a>
                                </CardContent>
                            </Card>
                        ))}

                        {oddVisibleCount < filteredOddData.length && (
                            <Button
                                onClick={handleLoadMoreOdd}
                                style={{
                                    marginTop: '20px',
                                    backgroundColor: '#388E3C',
                                    color: 'white',
                                }}
                            >
                                Plus des indicateurs (ODD)
                            </Button>
                        )}
                    </>
                )}
            </Container>
            <br />
            <br />
            <Footer />
        </div>
    );
}

export default IndicateurODD;